/**
 * The label an operator puts on a call the moment it ends.
 *
 * The registry is `crm.labels.calls` - the same labels the call list already
 * filters by - so this adds no second vocabulary to keep in step. What it adds
 * is the ONE moment when the answer is known: the operator has just put the
 * phone down and could say in one word how it went, and today that word has to
 * wait until they find the row again, by which time there are twenty of them.
 *
 * Held by `CallWindow`, which is mounted for the whole session, rather than by
 * the ended screen, which mounts per call. Two reasons and both are visible:
 * the registry is fetched once instead of once per call, and the chips are on
 * screen the instant the call ends rather than a request later.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { patchCall } from "../../../lib/calls";
import { toggleTag, type CrmLabel } from "../../../lib/crmLabels";
import { useCrmLabels } from "../../useCrmLabels";

export interface CallOutcomeApi {
  /** The registry, empty until it loads and legitimately empty forever if
   *  nobody has filled it in on the settings page. */
  labels: CrmLabel[];
  /** What is on this call right now, optimistically. */
  tags: string[];
  saving: boolean;
  /** The last write did not land, so the chips have been put back. */
  failed: boolean;
  toggle: (id: string) => void;
}

export function useCallOutcome(callLogId: string | null): CallOutcomeApi {
  const { labels } = useCrmLabels();
  const [tags, setTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  /**
   * What the last click sent, read by the next one.
   *
   * The same problem `LabelItems` solves: a tags patch carries the WHOLE set,
   * and two chips pressed in quick succession must compose. Reading `tags` from
   * the closure would give the second click the value from before the first,
   * and the second write would erase the first label.
   */
  const sentRef = useRef<string[]>([]);
  /** Which write is the latest, so a slow one landing after a fast one cannot
   *  put the chips back to a set the operator has already left. */
  const seqRef = useRef(0);

  // A new call is a blank slate. The row was created a moment ago by
  // `POST /calls` and carries no labels, so anything still here belongs to the
  // call before it - which on a run is a different company entirely.
  useEffect(() => {
    sentRef.current = [];
    seqRef.current += 1;
    setTags([]);
    setSaving(false);
    setFailed(false);
  }, [callLogId]);

  const toggle = useCallback(
    (id: string) => {
      // No row, nothing to label. The end screen hides the chips in that case;
      // this is the guard behind it rather than a second copy of the rule.
      if (!callLogId) return;
      const before = sentRef.current;
      const next = toggleTag(before, labels.calls, id);
      const seq = (seqRef.current += 1);
      sentRef.current = next;
      setTags(next);
      setFailed(false);
      setSaving(true);
      void patchCall(callLogId, { tags: next })
        .catch(() => {
          if (seqRef.current !== seq) return;
          // Put it back rather than leave a chip claiming a label the row does
          // not carry. It matters more here than it looks: on a run this chip
          // is what opens the next call, and an outcome that was never stored
          // would let the operator past a gate that had not been satisfied.
          sentRef.current = before;
          setTags(before);
          setFailed(true);
        })
        .finally(() => {
          if (seqRef.current === seq) setSaving(false);
        });
    },
    [callLogId, labels.calls],
  );

  return { labels: labels.calls, tags, saving, failed, toggle };
}
